/**
 * Floor Tool - Floor/Slab Creation by Sketch
 * 
 * Floors are created by sketching a closed boundary.
 * Features:
 * - Boundary sketch mode
 * - Slope arrows
 * - Multiple floor types
 * - Level association
 */

import { v4 as uuidv4 } from 'uuid';
import { FloorElement, FloorGeometryData, Layer } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export interface FloorToolOptions {
    floorType: string;
    thickness: number; // mm (override if needed, usually from type)
    levelId: string;
    offset: number; // mm
    structural: boolean;
}

export class FloorTool extends BaseTool {
    id = 'floor';
    name = 'Floor';
    cursor = 'crosshair';

    private points: THREE.Vector3[] = [];
    private currentPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;

        const snapped = this.snapToGrid(point);
        this.points.push(snapped);

        // Check if closed (clicked near start)
        if (this.points.length > 2) {
            const start = this.points[0];
            if (snapped.distanceTo(start) < 0.2) {
                // Close loop
                this.points.pop(); // Remove the last point which is close to start
                this.finishSketch();
                return;
            }
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            this.currentPoint = this.snapToGrid(point);
            this.updatePreview();
        }
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            if (this.points.length >= 3) {
                this.finishSketch();
            }
        } else if (event.key === 'Escape') {
            if (this.points.length > 0) {
                this.points = [];
                this.updatePreview();
            } else {
                super.onKeyDown(event);
            }
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (this.points.length > 0 && this.currentPoint) {
            const previewPoints = [...this.points, this.currentPoint];
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: previewPoints,
                closed: false,
                color: '#00ff00'
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    private finishSketch() {
        if (!this.context) return;

        // Convert 3D points to 2D boundary (assuming flat floor on level)
        const boundary: [number, number][] = this.points.map(p => [p.x, p.y]);

        // Default options
        const options: FloorToolOptions = {
            floorType: 'Floor: Generic 200mm',
            thickness: 200,
            levelId: 'Level 1', // Should get active level
            offset: 0,
            structural: false
        };

        const floor = createFloor(boundary, options);
        this.context.bimStore.addElement(floor);

        // Reset
        this.points = [];
        this.context.appStore.setToolPreview(null);
    }
}

export function createFloor(
    boundary: [number, number][], // Closed polygon of 2D points
    options: FloorToolOptions
): FloorElement {
    const layers = getFloorLayers(options.floorType);
    const thicknessM = layers.reduce((sum: number, layer: Layer) => sum + layer.thickness, 0);

    const geometryData: FloorGeometryData = {
        boundary: boundary,
        thickness: thicknessM,
        layers: layers,
        levelId: options.levelId,
        heightOffsetFromLevel: options.offset / 1000
    };

    const floor: FloorElement = {
        id: uuidv4(),
        category: 'Floors',
        type: options.floorType,
        parameters: {
            'Thickness': `${thicknessM.toFixed(2)} m`,
            'Level': options.levelId,
            'Height Offset From Level': `${options.offset} mm`,
            'Structural': options.structural,
            'Area': calculatePolygonArea(boundary).toFixed(2) + ' m²',
            'Perimeter': calculatePolygonPerimeter(boundary).toFixed(2) + ' m'
        },
        constraints: [],
        geometryData
    };

    return floor;
}

function calculatePolygonArea(points: [number, number][]): number {
    let area = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        area += points[i][0] * points[j][1];
        area -= points[j][0] * points[i][1];
    }
    return Math.abs(area) / 2;
}

function calculatePolygonPerimeter(points: [number, number][]): number {
    let perimeter = 0;
    const n = points.length;
    for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        const dx = points[j][0] - points[i][0];
        const dy = points[j][1] - points[i][1];
        perimeter += Math.sqrt(dx * dx + dy * dy);
    }
    return perimeter;
}

export const DEFAULT_FLOOR_TYPES = [
    'Floor: Generic 200mm',
    'Floor: Generic 300mm',
    'Floor: Wood Joist 235mm - Wood Finish',
    'Floor: Steel Bar Joist - VCT on Concrete',
    'Floor: Concrete Slab 150mm'
];

function getFloorLayers(floorType: string): Layer[] {
    switch (floorType) {
        case 'Floor: Generic 200mm':
            return [{ function: 'Structure', materialId: 'Generic', thickness: 0.2, wraps: false }];
        case 'Floor: Wood Joist 235mm - Wood Finish':
            return [
                { function: 'Finish 1', materialId: 'Wood Flooring', thickness: 0.015, wraps: false },
                { function: 'Structure', materialId: 'Wood Joist', thickness: 0.235, wraps: false }
            ];
        case 'Floor: Steel Bar Joist - VCT on Concrete':
            return [
                { function: 'Finish 1', materialId: 'VCT', thickness: 0.005, wraps: false },
                { function: 'Structure', materialId: 'Concrete', thickness: 0.1, wraps: false },
                { function: 'Structure', materialId: 'Steel Bar Joist', thickness: 0.3, wraps: false }
            ];
        default:
            return [{ function: 'Structure', materialId: 'Generic', thickness: 0.2, wraps: false }];
    }
}
